import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useCallback, useState } from 'react';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { ImageRatio } from '@diritto/common/base/constants';
import { BaseFileFiledDto } from '@diritto/common/base/base.dto';
import { isPC } from 'constants/layout';
import { EditCoverImageToolbar } from 'app-components/contents/editCoverImage/EditCoverImageToolbar';
import { useCoverImageEditor } from 'state-hooks/contents/useCoverImageEditor';
import { useUploadCoverImage } from 'state-hooks/contents/useUploadCoverImage';
import { getCoverImageBorder } from 'styles/common';
import { Loading } from 'ui-components/common/Loading';
import { useEditCoverImageWidth } from 'ui-hooks/useEditCoverImageWidth';
import { showAlertModal } from 'utils/modal';
import { ArrowIcon } from 'icons/ArrowIcon';

interface EditCoverImageProps {
  selectImagePath: string;
  onSubmit: (file: BaseFileFiledDto) => void;
  onBack: () => void;
}

export const EditCoverImage = ({ selectImagePath, onSubmit, onBack }: EditCoverImageProps) => {
  const [imageEditorRef, setImageEditorRef] = useState<HTMLDivElement | null>(null);
  const { width, height, setImageWrapperRef } = useEditCoverImageWidth();

  const {
    isLoaded,
    selectFontFamily,
    fontFamilies,
    addText,
    changeFontColor,
    changeFontStyle,
    getImageFile,
    clearImageEditor,
  } = useCoverImageEditor(imageEditorRef, selectImagePath);

  const { uploadImage } = useUploadCoverImage();

  const handleSubmit = useCallback(async () => {
    try {
      const file = await getImageFile();
      const res = await uploadImage(file);
      if (res) {
        await clearImageEditor();
        onSubmit(res);
      }
    } catch {
      showAlertModal('이미지 업로드에 실패했어요.');
    }
  }, [getImageFile, uploadImage, clearImageEditor, onSubmit]);

  return (
    <StyledContainer>
      <StyledTitleWrapper>
        <StyledBack onClick={onBack}>
          <ArrowIcon />
        </StyledBack>
        <StyledTitle>표지 꾸미기</StyledTitle>
        <StyledSave onClick={handleSubmit}>저장</StyledSave>
      </StyledTitleWrapper>
      <StyledEditor ref={setImageWrapperRef}>
        <StyledImageWrapper width={width} height={height}>
          <div ref={setImageEditorRef} />
          {!isLoaded && (
            <StyledLoading>
              <Loading />
            </StyledLoading>
          )}
        </StyledImageWrapper>
      </StyledEditor>
      <StyledEditCoverImageToolbar
        width={width}
        height={height}
        fontFamily={selectFontFamily}
        fontFamilies={fontFamilies}
        onClickAddText={addText}
        onChangeFontStyle={changeFontStyle}
        onChangeColor={changeFontColor}
        onClickClear={clearImageEditor}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledTitleWrapper = styled.div`
  padding: 16px;
  width: 100%;
  height: 56px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${Color.Gray300};
`;

const StyledSave = styled.div`
  flex: 1;
  text-align: right;
  cursor: pointer;
`;

const StyledBack = styled.div`
  flex: 1;
  cursor: pointer;
  align-self: stretch;
`;

const StyledTitle = styled.p`
  width: 100%;
  text-align: center;
  font-weight: ${FontWeight.Bold};
  ${Typography.XSmall};
  flex: 8;
  @media (${isPC}) {
    flex-shrink: 0;
  }
`;

const StyledEditor = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 30px 0;
  padding: 0 30px;
  @media (${isPC}) {
    flex-grow: 1;
    margin: 0 0;
  }
`;

const StyledImageWrapper = styled.div(
  ({ width }: { width: number; height: number }) => css`
    width: 100%;
    height: ${width * ImageRatio}px;
    position: relative;

    & .tui-image-editor-main {
      ${getCoverImageBorder()};

      &::before {
        position: initial; // position을 수정할경우 tui가 오작동 함
      }
    }

    @media (${isPC}) {
      width: 320px;
      height: 478px;
    }
  `,
);

const StyledLoading = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: ${Color.White};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledEditCoverImageToolbar = styled(EditCoverImageToolbar)(
  ({ width }: { width: number; height: number }) => css`
    width: ${width}px;
    height: 45px;
    margin-bottom: 30px;
    padding: 0 30px;
    @media (${isPC}) {
      width: 341px;
      height: 48px;
      padding: 0;
    }
  `,
);
